import { Condition, Immunization, Observation } from "@medplum/fhirtypes";
import { SmartDiagnosticReport, SmartProcedure } from "@metriport/fhir-sdk";
import type { EhrWritePath } from "../../../../domain/ehr";
import { MedicationWithRefs } from "../table-transforms/medication-types";
import {
  ConditionSeries,
  ImmunizationSeries,
  MedicationSeries,
  ObservationSeries,
  ProcedureSeries,
} from "../table-transforms/series-utils";
import { EnhancedMedicationSeries } from "../tables/medication";
import {
  isAllergyIntolerance,
  isConditionSeries,
  isDiagnosticReport,
  isEnhancedMedicationSeries,
  isImmunizationSeries,
  isMedicationSeries,
  isObservationSeries,
  isProcedureSeries,
} from "./type-guards";
import { decodeBase64AsUtf8 } from "../../shared-components/fhir-sections/shared";

/**
 * WRITE-BACK TRANSFORMER to support V1 write-back shape
 *
 * This module handles conversion from V2 data structures (ObservationSeries, etc.)
 * to V1 data structures for EHR write-back.
 *
 * WHY: V1 EHR write-back expects specific shapes:
 * - Vitals: GroupedVitals (with mostRecentObservation + sortedPoints)
 * - Labs: Raw Observation FHIR resource
 * - Medications: MedicationWithRefs (from EnhancedMedicationSeries)
 * - Notes: NoteData (decoded encounterText + date from DiagnosticReport)
 * - Procedures: Raw Procedure (pass-through)
 * - Allergies: Raw AllergyIntolerance (pass-through)
 * V2 uses unified Series structures that need to be transformed.
 */

// ============================================================================
// V1 TYPES (what EHR write-back expects)
// ============================================================================

type DataPoint = {
  value: number;
  date: string;
  unit?: string;
  bp?: { systolic: number; diastolic: number };
  id?: string;
  filename?: string;
};

type GroupedVitals = {
  title: string;
  mostRecentObservation: Observation;
  sortedPoints?: DataPoint[];
};

type GroupedConditions = {
  title: string;
  mostRecentCondition: Condition;
};

type GroupedImmunizations = {
  title: string;
  mostRecentImmunization: Immunization;
};

type GroupedMedications = MedicationWithRefs;

type NoteData = {
  encounterText: string;
  date: string;
};

// ============================================================================
// CONVERTERS - V2 → V1
// ============================================================================

function convertObservationSeriesToGroupedVitals(series: ObservationSeries): GroupedVitals {
  const mostRecentObservation = series.resources[series.resources.length - 1];
  if (!mostRecentObservation) {
    throw new Error("ObservationSeries must have at least one resource");
  }
  const sortedPoints: DataPoint[] = series.resources
    .filter(resource => !!resource.valueQuantity?.value)
    .map(resource => ({
      value: resource.valueQuantity?.value as number,
      date: resource.effectiveDateTime ?? resource.effectivePeriod?.start ?? "",
      unit: resource.valueQuantity?.unit?.replace(/[{()}]/g, ""),
      id: resource.id,
      filename: resource.extension?.find(ext => ext.valueString)?.valueString,
    }));
  return {
    title: series.codeDisplay,
    mostRecentObservation: mostRecentObservation as Observation,
    sortedPoints,
  };
}

function convertObservationSeriesToRawObservation(series: ObservationSeries): Observation {
  // For labs, backend expects raw Observation, not GroupedLabs wrapper
  const mostRecentObservation = series.resources[series.resources.length - 1];
  if (!mostRecentObservation) {
    throw new Error("ObservationSeries must have at least one resource");
  }
  return mostRecentObservation as Observation;
}

function convertConditionSeriesToGroupedConditions(series: ConditionSeries): GroupedConditions {
  const mostRecentCondition = series.resources[series.resources.length - 1];
  if (!mostRecentCondition) {
    throw new Error("ConditionSeries must have at least one resource");
  }
  return {
    title: series.codeDisplay,
    mostRecentCondition: mostRecentCondition as Condition,
  };
}

function convertImmunizationSeriesToGroupedImmunizations(
  series: ImmunizationSeries
): GroupedImmunizations {
  const mostRecentImmunization = series.resources[series.resources.length - 1];
  if (!mostRecentImmunization) {
    throw new Error("ImmunizationSeries must have at least one resource");
  }
  return {
    title: series.codeDisplay,
    mostRecentImmunization: mostRecentImmunization as Immunization,
  };
}

function convertMedicationSeriesToMedicationWithRefs(series: MedicationSeries): GroupedMedications {
  const mostRecent = series.resources[series.resources.length - 1];
  if (!mostRecent) {
    throw new Error("MedicationSeries must have at least one resource");
  }
  return mostRecent;
}

function convertEnhancedMedicationSeriesToMedicationWithRefs(
  series: EnhancedMedicationSeries
): GroupedMedications {
  return series.medicationWithRefs;
}

function convertDiagnosticReportToNoteData(report: SmartDiagnosticReport): NoteData {
  let encounterText = "";

  if (report.presentedForm && report.presentedForm.length > 0) {
    const texts: string[] = [];

    for (const form of report.presentedForm) {
      if (!form.data) continue;

      try {
        // Decode base64 data
        const decoded = decodeBase64AsUtf8(form.data);
        texts.push(decoded);
      } catch (error) {
        console.error("Failed to decode presentedForm data:", error);
      }
    }

    encounterText = texts.join("\n");
  }

  // Fallback to conclusion if no presentedForm data
  if (!encounterText) {
    encounterText = report.conclusion || "";
  }

  // Extract date
  const date = report.effectiveDateTime ?? report.effectivePeriod?.start ?? report.issued ?? "";

  return {
    encounterText,
    date,
  };
}

function convertProcedureSeriesToRawProcedure(series: ProcedureSeries): SmartProcedure {
  const mostRecentProcedure = series.resources[series.resources.length - 1];
  if (!mostRecentProcedure) {
    throw new Error("ProcedureSeries must have at least one resource");
  }
  return mostRecentProcedure;
}
// ============================================================================
// MAIN TRANSFORMER - Route by path
// ============================================================================

/**
 * Converts V2 data structures to V1 format for EHR write-back.
 *
 * @param originalData - The V2 data structure (ObservationSeries, ConditionSeries, etc.)
 * @param path - The EHR write path (determines which converter to use)
 * @returns V1-compatible data structure, or original data if already V1 or unknown
 */
export function transformV2ToV1WriteBackFormat(originalData: unknown, path: EhrWritePath): object {
  if (!isV2Format(originalData)) return originalData as object;
  switch (path) {
    case "vitals":
      if (isObservationSeries(originalData)) {
        return convertObservationSeriesToGroupedVitals(originalData);
      }
      break;
    case "lab":
      if (isObservationSeries(originalData)) {
        return convertObservationSeriesToRawObservation(originalData);
      }
      break;
    case "condition":
      if (isConditionSeries(originalData)) {
        return convertConditionSeriesToGroupedConditions(originalData);
      }
      break;
    case "immunization":
      if (isImmunizationSeries(originalData)) {
        return convertImmunizationSeriesToGroupedImmunizations(originalData);
      }
      break;
    case "medication":
      if (isEnhancedMedicationSeries(originalData)) {
        return convertEnhancedMedicationSeriesToMedicationWithRefs(originalData);
      }
      if (isMedicationSeries(originalData)) {
        return convertMedicationSeriesToMedicationWithRefs(originalData);
      }
      break;
    case "procedure":
      if (isProcedureSeries(originalData)) {
        return convertProcedureSeriesToRawProcedure(originalData);
      }
      break;
    case "allergy":
      if (isAllergyIntolerance(originalData)) return originalData;
      break;
    case "note":
      if (isDiagnosticReport(originalData)) {
        return convertDiagnosticReportToNoteData(originalData);
      }
      break;
    default:
      console.warn(`No legacy transformer for path: ${path}, passing through original data`);
  }
  // Fallback: return original if no converter matches
  return originalData as object;
}

/**
 * Helper to detect if data is V2 format (any Series type or raw FHIR resource)
 */
function isV2Format(data: unknown): boolean {
  return (
    isObservationSeries(data) ||
    isConditionSeries(data) ||
    isImmunizationSeries(data) ||
    isMedicationSeries(data) ||
    isEnhancedMedicationSeries(data) ||
    isProcedureSeries(data) ||
    isDiagnosticReport(data) ||
    isAllergyIntolerance(data)
  );
}
